/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ConfigurationTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Calls Routing module.
----------------------------------------------------------------*/


import React from 'react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { Toggle } from '../../components/ui/Toggle';
import { Loader2, Settings } from 'lucide-react';

interface DynamicRoutingConfig {
    digitsMatch?: string | null;
    deleteUsedRecords: boolean;
    expirationMinutes: number;
    onlyKeepMissedCalls: boolean;
}

interface ConfigurationTabProps {
    config: DynamicRoutingConfig | null;
    isLoading: boolean;
    onConfigChange: <K extends keyof DynamicRoutingConfig>(field: K, value: DynamicRoutingConfig[K]) => void;
}

export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
    config,
    isLoading,
    onConfigChange
}) => {
    const { t } = useI18n();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!config) {
        return (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
                {t('common.error_loading', 'Error loading configuration')}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <FormBox
                title={t('dynamic_routing.general_settings', 'General Settings')}
                icon={<Settings className="h-5 w-5" />}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                    {/* Row 1: Digits Match | Input | Delete Used Records | Toggle */}
                    <SimpleTooltip
                        content={t('dynamic_routing.digits_match_help', "It allows to define the number of digits to be extracted from the caller's number (from right to left), to later search for matches in the dynamic routing list. If left blank, the entire caller's number will be used to search for matches.")}
                        title={t('dynamic_routing.digits_match', 'Digits Match')}
                        position="top"
                        maxWidth="350px"
                    >
                        <Label htmlFor="digitsMatch" className="text-left">
                            {t('dynamic_routing.digits_match', 'Digits Match')}
                        </Label>
                    </SimpleTooltip>
                    <Input
                        id="digitsMatch"
                        type="number"
                        min={1}
                        max={20}
                        placeholder=""
                        value={config.digitsMatch || ''}
                        onChange={(e) => onConfigChange('digitsMatch', e.target.value || null)}
                    />

                    <SimpleTooltip
                        content={t('dynamic_routing.delete_used_records_help', 'If checked, the record will be deleted from the dynamic routing list when the callback is answered.')}
                        title={t('dynamic_routing.delete_used_records', 'Delete Used Records')}
                        position="top"
                        maxWidth="350px"
                    >
                        <Label htmlFor="deleteUsedRecords" className="text-left">
                            {t('dynamic_routing.delete_used_records', 'Delete Used Records')}
                        </Label>
                    </SimpleTooltip>
                    <Toggle
                        id="deleteUsedRecords"
                        value={config.deleteUsedRecords}
                        onChange={(value) => onConfigChange('deleteUsedRecords', value)}
                    />

                    {/* Row 2: Expiration Minutes | Input | Only Keep Missed Calls | Toggle */}
                    <SimpleTooltip
                        content={t('dynamic_routing.expiration_help', 'How long to verify the callback before expiration.')}
                        title={t('dynamic_routing.expiration_minutes', 'Expiration (Minutes)')}
                        position="top"
                        maxWidth="300px"
                    >
                        <Label htmlFor="expirationMinutes" className="text-left">
                            {t('dynamic_routing.expiration_minutes', 'Expiration (Minutes)')}
                        </Label>
                    </SimpleTooltip>
                    <Input
                        id="expirationMinutes"
                        type="number"
                        min={1}
                        value={config.expirationMinutes}
                        onChange={(e) => onConfigChange('expirationMinutes', parseInt(e.target.value) || 0)}
                    />

                    <SimpleTooltip
                        content={t('dynamic_routing.only_keep_missed_calls_help', 'If checked, only calls (outgoing) that are missed by the called party will be saved in the dynamic routing list.')}
                        title={t('dynamic_routing.only_keep_missed_calls', 'Only Keep Missed Calls')}
                        position="top"
                        maxWidth="350px"
                    >
                        <Label htmlFor="onlyKeepMissedCalls" className="text-left">
                            {t('dynamic_routing.only_keep_missed_calls', 'Only Keep Missed Calls')}
                        </Label>
                    </SimpleTooltip>
                    <Toggle
                        id="onlyKeepMissedCalls"
                        value={config.onlyKeepMissedCalls}
                        onChange={(value) => onConfigChange('onlyKeepMissedCalls', value)}
                    />
                </div>
            </FormBox>
        </div>
    );
};
